import { forwardRef } from 'react';

import { XStack, YStack } from '@/components/ui/layout/layout';
import {
  ROUND_ICON_BUTTON_EDGE_INSET,
  RoundIconButton,
} from '@/design-system-v2/round-icon-button';
import { cn } from '@/lib/utils';

type ChildrenProps = { children: React.ReactNode; className?: string };

export const Root = ({ children, className }: ChildrenProps) => (
  <YStack
    className={cn(
      'shell ambient-sidebar group/sidebar relative w-full gap-1 border-r border-border-subtle px-[var(--panel-gutter)] [--panel-gutter:12px] [--row-pad:8px]',
      className
    )}
  >
    {children}
  </YStack>
);

export const Header = ({ children, className }: ChildrenProps) => (
  <XStack
    className={cn(
      'h-16 shrink-0 items-center justify-between gap-2 px-[var(--row-pad)]',
      className
    )}
  >
    {children}
  </XStack>
);

export const Title = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <span
    className={cn(
      // `truncate` clips, so the padding leaves room inside it for a title button's focus ring, and the negative margin keeps the text in place
      '-m-1 min-w-0 truncate p-1 text-lg font-medium text-foreground',
      className
    )}
  >
    {children}
  </span>
);

export const PanelTitle = ({ children }: { children: React.ReactNode }) => (
  <Title className="font-bold">{children}</Title>
);

export const Actions = ({ children }: { children: React.ReactNode }) => (
  <XStack
    className={cn(
      'shrink-0 items-center gap-1',
      ROUND_ICON_BUTTON_EDGE_INSET.sm
    )}
  >
    {children}
  </XStack>
);

export const Action = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof RoundIconButton>
>((props, ref) => <RoundIconButton ref={ref} size="sm" {...props} />);
Action.displayName = 'SidePanel.Action';

export const PrimaryAction = ({ children }: { children: React.ReactNode }) => (
  <YStack className="shrink-0 pb-[18px]">{children}</YStack>
);

export const Toolbar = ({ children, className }: ChildrenProps) => (
  <YStack className={cn('gap-2 pb-3', className)}>{children}</YStack>
);
